/**
 * 单掷金币 g（model 2，docs/economy-g-model.md §2.2、§2.4）：库里存千分之一金币的无损十进制串 `g_milli`，
 * 界面上按金币填（最多 3 位小数）。两个方向都只做字符串拼接，不经过 Number——g 常年超过 2^53
 * （迁移版本 g = E_milli × B），过一次浮点末几位就变了。
 */
import { fmtFull } from "./big.ts";

/** 与服务端 econ-unit.ts G_MILLI_MAX_DIGITS 一致：超过就被写接口拒 */
export const G_MILLI_MAX_DIGITS = 85;

export type GCoinsParse =
  | { ok: true; milli: string | null }
  | { ok: false; reason: "format" | "decimals" | "zero" | "digits" };

/**
 * 金币文本 → 千分位规范串。空 = null（清空，回到曲线）。
 * 允许千分位逗号；小数超过 3 位直接拒（不四舍五入，免得存进去的不是人敲的那个数）；结果必须 ≥ 1 千分位。
 */
export function parseGCoins(raw: string): GCoinsParse {
  const s = raw.trim().replace(/,/g, "");
  if (s === "") return { ok: true, milli: null };
  const m = /^([0-9]*)(?:\.([0-9]*))?$/.exec(s);
  if (!m || (m[1] === "" && !m[2])) return { ok: false, reason: "format" };
  const frac = m[2] ?? "";
  if (frac.length > 3) return { ok: false, reason: "decimals" };
  const milli = `${m[1]}${frac.padEnd(3, "0")}`.replace(/^0+/, "");
  if (milli === "") return { ok: false, reason: "zero" };
  if (milli.length > G_MILLI_MAX_DIGITS) return { ok: false, reason: "digits" };
  return { ok: true, milli };
}

/** 千分位规范串 → 金币文本（不分组，给输入框）："3100" → "3.1"，"5" → "0.005" */
export function gMilliToCoins(milli: string): string {
  const p = milli.padStart(4, "0");
  const int = p.slice(0, -3).replace(/^0+(?=\d)/, "");
  const frac = p.slice(-3).replace(/0+$/, "");
  return frac ? `${int}.${frac}` : int;
}

/** 千分位规范串 → 四舍五入到整金币、带千分位分组（只读显示；实发金币是整数，小数只在编辑时露出）："1234567500" → "1,234,568" */
export function fmtGMilli(milli: string | null | undefined): string {
  if (milli == null || milli === "") return "—";
  return fmtFull(((BigInt(milli) + 500n) / 1000n).toString());
}

/** 千分位串的 log10（只要比例，取前 15 位有效数字足够；g 可能远超 2^53，不整体转 Number） */
function log10Milli(milli: string): number {
  const head = milli.slice(0, 15);
  return Math.log10(Number(head)) + (milli.length - head.length);
}

/** 10^l 取整成千分位串，最小 1 */
function milliOfLog10(l: number): string {
  if (l < 15) return String(Math.max(1, Math.round(10 ** l)));
  const int = Math.floor(l) - 14;
  return String(Math.round(10 ** (l - int))) + "0".repeat(int);
}

/**
 * 推荐 g：按前面已填的关（星）的走势外推。取第 at 位之前最近的 6 个已填值，按首尾算每位的平均增长倍数，
 * 从最近那个已填值乘到第 at 位、四舍五入到整金币；前面只有一个已填值就沿用它；前面一个都没有就取后面最近的已填值。
 * seq[i] 是第 i+1 位的 g_milli（null = 没填）。
 */
export function suggestGMilli(seq: readonly (string | null | undefined)[], at: number): string | null {
  const before: [number, string][] = [];
  for (let i = Math.min(at - 1, seq.length) - 1; i >= 0 && before.length < 6; i--) {
    const v = seq[i];
    if (v) before.unshift([i, v]);
  }
  if (before.length === 0) return seq.slice(at).find((v) => !!v) ?? null;
  const [lastI, last] = before[before.length - 1]!;
  if (before.length === 1) return last;
  const [firstI, first] = before[0]!;
  const step = (log10Milli(last) - log10Milli(first)) / (lastI - firstI);
  // 推荐值取整金币：外推出来的小数没有意义，填进去还会露出 0.966 这种尾巴
  const m = BigInt(milliOfLog10(log10Milli(last) + step * (at - 1 - lastI)));
  const coins = (m + 500n) / 1000n;
  return String((coins > 0n ? coins : 1n) * 1000n);
}
